// Frontend plugin UI loader (Stage 11).
//
// Fetches a plugin's UI bundle from the Stage-10 backend endpoint
// (`/api/plugins/<id>/ui/<file>`), evaluates it as an ES module via
// `import()`, and returns the namespace.
//
// The backend transpiles `.ts` / `.tsx` to JS on the fly; the browser
// only ever receives ESM JS. Plugin modules are cached by `pluginId`
// for the lifetime of the page; the Stage-11 reactive provider
// observes `extensions.reload` RPCs and invalidates the cache.

import { getApiBaseUrl } from "../../store";

const cache = new Map<string, Promise<unknown>>();

/// Resolve the absolute URL the browser fetches a plugin asset from.
/// MUST be absolute against the backend: in the packaged Tauri app the page
/// origin is `tauri://localhost` (the asset protocol), NOT the backend, so a
/// relative `/api/...` would resolve to the webview and 404 (plugin UI never
/// loads → modules missing from the nav). `getApiBaseUrl()` returns the backend
/// origin in both Tauri and browser/dev.
export function pluginAssetUrl(pluginId: string, assetPath: string): string {
  const safeId = encodeURIComponent(pluginId);
  // Don't encode the path separators — keep nested asset structure
  // legible. The backend's traversal guard catches escapes.
  const safePath = assetPath
    .split("/")
    .map((seg) => encodeURIComponent(seg))
    .join("/");
  return `${getApiBaseUrl()}/api/plugins/${safeId}/ui/${safePath}`;
}

/// Load a plugin's main UI entry. Returns the module namespace; the
/// caller picks named exports (e.g. `CompaniesList`) from it.
///
/// Cached: subsequent calls with the same `(pluginId, entry)` return
/// the same Promise. Call `invalidatePlugin(pluginId)` to force a
/// re-fetch (e.g. after a hot reload).
export async function loadPluginModule<T = Record<string, unknown>>(
  pluginId: string,
  entry = "index.tsx",
): Promise<T> {
  const cacheKey = `${pluginId}::${entry}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached as Promise<T>;
  const url = pluginAssetUrl(pluginId, entry);
  const promise = import(/* @vite-ignore */ url);
  cache.set(cacheKey, promise);
  return promise as Promise<T>;
}

/// Resolve the URL the browser fetches a source's transpiled auth screen from
/// (Stage 2b, DEC-3). The backend serves `<extensions_dir>/<id>/auth/screen.tsx`
/// transpiled to ESM JS at this route.
export function sourceAuthScreenUrl(sourceId: string): string {
  return `${getApiBaseUrl()}/api/sources/${encodeURIComponent(sourceId)}/auth/screen.js`;
}

/// The host-owned OAuth/connect start route for a source (Stage 6). A top-level
/// navigation here hands the whole ceremony to the host (DEC-15).
export function sourceStartUrl(sourceId: string): string {
  return `${getApiBaseUrl()}/auth/sources/${encodeURIComponent(sourceId)}/start`;
}

/// Load a source's per-source auth screen (DEC-3). Returns the module namespace;
/// its `default` export is the screen component (OAuth: a connect button that
/// navigates to `/auth/sources/<id>/start`; phone_code: the multi-step form that
/// drives `source.auth.submit` / `source.auth.exec` over WS). Cached per source.
export async function loadSourceAuthScreen<T = Record<string, unknown>>(
  sourceId: string,
): Promise<T> {
  const cacheKey = `source-auth::${sourceId}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached as Promise<T>;
  const promise = import(/* @vite-ignore */ sourceAuthScreenUrl(sourceId));
  cache.set(cacheKey, promise);
  return promise as Promise<T>;
}

/// Drop every cached module for `pluginId`. The next `loadPluginModule`
/// call re-fetches from the network. Used by the reactive
/// AppRuntimeProvider when the host notifies us that the plugin's
/// code changed.
export function invalidatePlugin(pluginId: string): void {
  for (const key of cache.keys()) {
    if (key.startsWith(`${pluginId}::`)) cache.delete(key);
  }
}

/// Drop the entire plugin cache. Used on a global "all plugins
/// reload" event (e.g. operator restart) when targeted invalidation
/// isn't practical.
export function invalidateAllPlugins(): void {
  cache.clear();
}
